import { HttpError } from '../types';

export type ParsedApiError = {
  errorCode: string | undefined;
  errorMessage: string;
  // From the websheet 400 body — lets the UI pick the paired vs linked
  // (Family Plan) localisation variant. Absent when the backend omits it
  // (e.g. INVALID_STATE) or on legacy "code,message" payloads.
  subscriptionType?: string;
  // Verbatim carrier/PSAP feedback on a VoWiFi 422 (UES-11807). Rendered
  // as-is by the websheet — never parsed or localised.
  errorDetails?: string;
};

export type ParsedError = ParsedApiError & {
  errorMsg: string;
  statusCode: number;
};

export const parseApiError = (errorMsg: string): ParsedApiError => {
  try {
    const parsed = JSON.parse(errorMsg);

    if (Array.isArray(parsed) && parsed.length > 0) {
      return { errorCode: parsed[0].type, errorMessage: parsed[0].msg };
    }

    if (parsed?.detail) {
      const d = Array.isArray(parsed.detail) ? parsed.detail[0] : parsed.detail;
      return { errorCode: d?.type, errorMessage: d?.msg ?? errorMsg };
    }

    if (parsed?.error_code) {
      return {
        errorCode: String(parsed.error_code),
        errorMessage: parsed.error ?? errorMsg,
        subscriptionType: parsed.subscription_type,
        errorDetails: parsed.error_details,
      };
    }
  } catch {
    // not JSON — fall through
  }

  const i = errorMsg.indexOf(',');
  if (i !== -1) {
    return {
      errorCode: errorMsg.slice(0, i),
      errorMessage: errorMsg.slice(i + 1),
    };
  }

  return { errorCode: undefined, errorMessage: errorMsg };
};

export const parseError = (err: unknown): ParsedError => {
  const errorMsg = (err as Error | TypeError).message;
  const statusCode = err instanceof HttpError ? err.statusCode ?? 500 : 500;

  // Errors that carry an explicit `code` property (e.g. SdkUnavailableError)
  // take precedence over message-based parsing — the source already classified
  // the failure, so we don't need to infer it from the string.
  const explicitCode = (err as { code?: string })?.code;
  if (explicitCode) {
    return {
      errorMsg,
      errorCode: explicitCode,
      errorMessage: errorMsg,
      statusCode,
    };
  }

  const { errorCode, errorMessage, subscriptionType, errorDetails } =
    parseApiError(errorMsg);
  return {
    errorMsg,
    errorCode,
    errorMessage,
    subscriptionType,
    errorDetails,
    statusCode,
  };
};
